import {
  ACCOUNT_API_PREFIX,
  ACCOUNT_STORAGE_KEY,
  AccountMeResponseSchema,
  AccountProgressBodySchema,
  AccountSessionResponseSchema,
  PASSWORD_MIN_LENGTH,
  PasswordSchema,
  type ProgressState,
  PSEUDO_MAX_LENGTH,
  PSEUDO_MIN_LENGTH,
  PseudoSchema,
} from '@md/shared';

/**
 * Compte joueur optionnel (pseudonyme + mot de passe, rien d'autre).
 *
 * Le jeu reste entièrement jouable sans compte : ce service ne fait que
 * synchroniser la progression locale avec le serveur quand une session est
 * ouverte. La session (`{ token, pseudo }`) vit en `localStorage`, le jeton
 * part en `Authorization: Bearer …` — jamais de cookie.
 *
 * Toute réponse du serveur passe par zod avant d'être crue.
 */

export type AccountState = { readonly pseudo: string } | null;

export type AccountAuthResult =
  | { readonly ok: true; readonly progress: ProgressState }
  | { readonly ok: false; readonly message: string };

export interface AccountService {
  /** Session courante (`null` = pas connecté). */
  state(): AccountState;
  register(pseudo: string, password: string): Promise<AccountAuthResult>;
  login(pseudo: string, password: string): Promise<AccountAuthResult>;
  /** Révoque la session côté serveur et oublie le jeton local. */
  logout(): Promise<void>;
  /**
   * Pousse la progression locale et renvoie la fusion calculée par le
   * serveur — `null` hors session ou si le réseau est indisponible (la
   * progression locale reste alors la seule vérité, rien n'est perdu).
   */
  sync(progress: ProgressState): Promise<ProgressState | null>;
  /** Valide la session mémorisée au démarrage. `false` = jeton oublié. */
  restore(): Promise<boolean>;
  subscribe(listener: (state: AccountState) => void): () => void;
}

export interface AccountServiceOptions {
  /** Stockage injectable (tests) ; par défaut `window.localStorage`. */
  readonly storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | undefined;
  /** Injectable pour les tests. */
  readonly fetch?: typeof globalThis.fetch;
  readonly baseUrl?: string;
}

interface StoredAccount {
  readonly token: string;
  readonly pseudo: string;
}

/** Message d'erreur affichable pour une saisie invalide, `null` si valide. */
export function credentialsError(pseudo: string, password: string): string | null {
  if (!PseudoSchema.safeParse(pseudo).success) {
    return `Pseudonyme : ${PSEUDO_MIN_LENGTH} à ${PSEUDO_MAX_LENGTH} caractères — lettres, chiffres, « . _ - ».`;
  }
  if (!PasswordSchema.safeParse(password).success) {
    return `Mot de passe : ${PASSWORD_MIN_LENGTH} caractères minimum.`;
  }
  return null;
}

/** Traduit un code d'erreur du serveur en message affichable. */
function serverMessage(status: number, code: string | undefined): string {
  if (code === 'pseudo_taken') {
    return 'Ce pseudonyme est déjà pris.';
  }
  if (code === 'invalid_credentials') {
    return 'Pseudonyme ou mot de passe incorrect.';
  }
  if (code === 'too_many_attempts') {
    return 'Trop de tentatives — réessayez dans une minute.';
  }
  return `Le serveur a répondu ${status}.`;
}

function safeLocalStorage(): Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | undefined {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

export function createAccountService(options: AccountServiceOptions = {}): AccountService {
  const storage = options.storage ?? safeLocalStorage();
  const doFetch = options.fetch ?? globalThis.fetch.bind(globalThis);
  const base = options.baseUrl ?? '';
  const listeners = new Set<(state: AccountState) => void>();

  let session: StoredAccount | null = load();

  function load(): StoredAccount | null {
    try {
      const raw = storage?.getItem(ACCOUNT_STORAGE_KEY);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw) as Partial<StoredAccount>;
      if (typeof parsed.token === 'string' && typeof parsed.pseudo === 'string') {
        return { token: parsed.token, pseudo: parsed.pseudo };
      }
    } catch {
      // Entrée corrompue ou stockage inaccessible : on repart déconnecté.
    }
    return null;
  }

  function persist(next: StoredAccount | null): void {
    session = next;
    try {
      if (next === null) {
        storage?.removeItem(ACCOUNT_STORAGE_KEY);
      } else {
        storage?.setItem(ACCOUNT_STORAGE_KEY, JSON.stringify(next));
      }
    } catch {
      // Écriture refusée : la session vit en mémoire pour cet onglet.
    }
    for (const listener of listeners) {
      listener(service.state());
    }
  }

  async function authenticate(
    path: '/register' | '/login',
    pseudo: string,
    password: string,
  ): Promise<AccountAuthResult> {
    const invalid = credentialsError(pseudo, password);
    if (invalid !== null) {
      return { ok: false, message: invalid };
    }
    let response: Response;
    try {
      response = await doFetch(`${base}${ACCOUNT_API_PREFIX}${path}`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ pseudo: pseudo.trim(), password }),
      });
    } catch {
      return { ok: false, message: 'Serveur injoignable — réessayez plus tard.' };
    }
    if (!response.ok) {
      const code = await errorCode(response);
      return { ok: false, message: serverMessage(response.status, code) };
    }
    const parsed = AccountSessionResponseSchema.safeParse(await response.json());
    if (!parsed.success) {
      return { ok: false, message: 'Réponse du serveur illisible.' };
    }
    persist({ token: parsed.data.token, pseudo: parsed.data.pseudo });
    return { ok: true, progress: parsed.data.progress };
  }

  async function errorCode(response: Response): Promise<string | undefined> {
    try {
      const body = (await response.json()) as { error?: unknown };
      return typeof body.error === 'string' ? body.error : undefined;
    } catch {
      return undefined;
    }
  }

  const service: AccountService = {
    state(): AccountState {
      return session === null ? null : { pseudo: session.pseudo };
    },

    register(pseudo: string, password: string): Promise<AccountAuthResult> {
      return authenticate('/register', pseudo, password);
    },

    login(pseudo: string, password: string): Promise<AccountAuthResult> {
      return authenticate('/login', pseudo, password);
    },

    async logout(): Promise<void> {
      const current = session;
      persist(null);
      if (current === null) {
        return;
      }
      try {
        await doFetch(`${base}${ACCOUNT_API_PREFIX}/logout`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${current.token}` },
        });
      } catch {
        // Serveur injoignable : le jeton local est déjà oublié, la session
        // serveur expirera d'elle-même — rien à faire de plus.
      }
    },

    async sync(progress: ProgressState): Promise<ProgressState | null> {
      const current = session;
      if (current === null) {
        return null;
      }
      let response: Response;
      try {
        response = await doFetch(`${base}${ACCOUNT_API_PREFIX}/progress`, {
          method: 'PUT',
          headers: {
            'content-type': 'application/json',
            Authorization: `Bearer ${current.token}`,
          },
          body: JSON.stringify({ progress }),
        });
      } catch {
        return null;
      }
      if (response.status === 401) {
        // Session révoquée depuis un autre appareil : on redevient local.
        persist(null);
        return null;
      }
      if (!response.ok) {
        return null;
      }
      const parsed = AccountProgressBodySchema.safeParse(await response.json());
      return parsed.success ? parsed.data.progress : null;
    },

    async restore(): Promise<boolean> {
      const current = session;
      if (current === null) {
        return false;
      }
      let response: Response;
      try {
        response = await doFetch(`${base}${ACCOUNT_API_PREFIX}/me`, {
          headers: { Authorization: `Bearer ${current.token}` },
        });
      } catch {
        // Hors ligne : on garde la session, elle sera revalidée plus tard.
        return true;
      }
      if (response.status === 401) {
        persist(null);
        return false;
      }
      if (!response.ok) {
        return true;
      }
      const parsed = AccountMeResponseSchema.safeParse(await response.json());
      if (parsed.success && parsed.data.pseudo !== current.pseudo) {
        persist({ token: current.token, pseudo: parsed.data.pseudo });
      }
      return true;
    },

    subscribe(listener: (state: AccountState) => void): () => void {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };

  return service;
}
